/**
 * These are the style props we need to compute off of
 * our textarea ref.  These props will effect the height
 * of our textarea as it resizes.
 */
const SIZING_STYLE: string[] = [
  'letter-spacing',
  'line-height',
  'padding-top',
  'padding-bottom',
  'font-size',
  'width',
  'padding-left',
  'padding-right',
  'box-sizing'
];
/**
 * Initial styles to be added to our proxy textarea
 */
const INITIAL_CLONE_STYLES = `
  min-height:0 !important;
  max-height:none !important;
  height:0 !important;
  visibility:hidden !important;
  overflow:hidden !important;
  position:absolute !important;
  z-index:-1000 !important;
  top:0 !important;
  right:0 !important;
`;
interface ITextAreaComputedStyles {
  sizing: string;
  borderSize: number;
  padding: number;
}
interface INewTextAreaStyles {
  height: number;
  minHeight: number | undefined;
  maxHeight: number | undefined;
  overflowY: string | undefined;
}

function calculateTextAreaStyles(
  node: HTMLTextAreaElement
): ITextAreaComputedStyles {
  // get computed styles from nodeRef
  const computedStyle = window.getComputedStyle(node);
  // calculate border width
  const borderSize =
    parseInt(computedStyle.getPropertyValue('border-bottom-width'), 10) +
      parseInt(computedStyle.getPropertyValue('border-top-width'), 10) || 0;
  // calculate padding
  const padding =
    parseInt(computedStyle.getPropertyValue('padding-top'), 10) +
      parseInt(computedStyle.getPropertyValue('padding-bottom'), 10) || 0;
  // build string of styles that can effect new sizing
  const sizing = SIZING_STYLE.map(
    styleName => `${styleName}:${computedStyle.getPropertyValue(styleName)}`
  ).join(';');
  // object to be added to initial textarea clone
  return {
    sizing,
    borderSize,
    padding
  };
}

export default function getCalculatedTextAreaHeight(
  node: HTMLTextAreaElement,
  clone: HTMLTextAreaElement,
  minRows: number | null = null,
  maxRows: number | null = null
): INewTextAreaStyles {
  // eslint no param reassign
  const cloned = clone;
  const { sizing, borderSize, padding } = calculateTextAreaStyles(node);
  // set hidden textarea clone styles to match visible textarea
  cloned.setAttribute('style', `${sizing};${INITIAL_CLONE_STYLES}`);
  // set clone text value to mirror visible text area value
  cloned.value = node.value || node.defaultValue || node.placeholder || '';
  // return inner height of clone to be set as visible height
  const height = cloned.scrollHeight + borderSize;
  let minHeight: number | undefined;
  let maxHeight: number | undefined;
  let overflowY: string | undefined;

  if (minRows !== null || maxRows !== null) {
    // get height of a single row
    cloned.value = ' ';
    const singleRowHeight = cloned.scrollHeight - padding;
    // if we have minRows
    if (minRows !== null) {
      // min height should be height of a single row * min number of rows
      minHeight = singleRowHeight * minRows + borderSize + padding;
    }
    // if we have maxRows
    if (maxRows !== null) {
      maxHeight = singleRowHeight * maxRows + borderSize + padding;
      // if we have more text than our max height, add scroll bar
      overflowY = height > maxHeight ? 'visible' : 'hidden';
    }
  }
  return { height, minHeight, maxHeight, overflowY };
}
